/*
 * Wmb, Worldline Merchant Billing.
 * Copyright (C) 2017-2019 Worldline
 * All Rights Reserved
 * This product is protected by copyright. Any copying or distribution
 * is forbidden without the agreement of Worldline.
 */
import { NGXLogger } from 'ngx-logger';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {MatSnackBar} from '@angular/material/snack-bar';
import { NgForm } from '@angular/forms';
import { ConfigurationService } from './configuration.service';
import { Subject } from 'rxjs';

export type MessageSeverity = 'success' | 'info' | 'warn' | 'error';

@Injectable()
export class NotifMessageService {

    constructor(
        private messageService: MatSnackBar,
        private log: NGXLogger,
        private translate: TranslateService,
        private configService: ConfigurationService
    ) {
    }

    messageConfig: {
        duration: 3000,
        horizontalPosition: "end",
        verticalPosition: "top"
    }

    /**
     * Afficher une popup de notification
     * @param title le titre de la popup (traduction prise en charge)
     * @param detail le texte de la popup (traduction prise en charge)
     * @param severity le type de notification (success, info, warn ou error)
     */
    public notify(title: string, severity: MessageSeverity): void {
        // on attends la disponibilite des traductions en cours de chargement pour obtenir le titre ...
        let translatedTitle: string = null;
        if (title) {
            this.translate.get(title).subscribe((value) => {
                translatedTitle = value;
                switch (severity) {
                    case 'info':
                        this.messageService.open(translatedTitle, null, {
                            duration: 3000,
                            horizontalPosition: "end",
                            verticalPosition: "top",
                            panelClass: ['bg-primary']
                        });
                        break;
                    case 'success':
                        this.messageService.open(translatedTitle, null, {
                            duration: 3000,
                            horizontalPosition: "end",
                            verticalPosition: "top",
                            panelClass: ['bg-success']
                        });
                        break;
                    case 'error':
                        this.messageService.open(translatedTitle, null, {
                            duration: 3000,
                            horizontalPosition: "end",
                            verticalPosition: "top",
                            panelClass: ['bg-danger']
                        });
                        break;
                    case 'warn':
                        this.messageService.open(translatedTitle, null, {
                            duration: 3000,
                            horizontalPosition: "end",
                            verticalPosition: "top",
                            panelClass: ['bg-warning']
                        });
                        break;
                    default:
                        this.messageService.open(translatedTitle, null, {
                            duration: 3000,
                            horizontalPosition: "end",
                            verticalPosition: "top",
                            panelClass: ['bg-primary']
                        });
                        break;
                }
            });
        } else {
            this.log.error(`notify(...) was called without a title, not notifying.`);
        }
    }

    public clearNotifs(): void {
        this.messageService.dismiss();
    }

    // -------------------------------------------------------------------
    // Fonctions pratiques
    public notifyInfo(message: string): void {
        this.notify(message, 'info');
    }

    public notifyWarn(message: string): void {
        this.notify(message, 'warn');
    }

    public notifySuccess(message: string): void {
        this.notify(message, 'success');
    }

    public notifyErrorWithoutDetail(message: string): void {
        this.notify(message, 'error');
    }

    /**
     *
     * @param message Une clé de traduction
     * @param apiError l'objet erreur retourné par le client http lors d'un appel vers l'api
     * @param translateParams un tableau associatif contenant les détails pour la traduction.
     * Exemple : {"folderName": "dossier1"}
     * @param ngForm un formulaire dans lequel on veut afficher les champs en erreur
     * @param showDetailsInPopup un boolean (defaut false) indiquant si on veut afficher la popup de détails d'erreur
     * @param duration la durée d'affichage de la notification, 0 = notification persistente (valeur par défaut).
     */
    public notifyErrorWithDetailFromApi(message: string, apiError: any, ngForm?: NgForm,
        showDetailsInPopup: boolean = false, duration: number = 0): void {

        // On commence par récupérer une traduction, on sait qu'ensuite elles sont toutes disponibles
        this.translate.get(message).subscribe(
            msg => {

                // Affichage de la notif
                if (apiError && apiError.error && apiError.error.status) {

                    // Préparation du lien dans la notif
                    let action: { action: () => void };
                    let detailAppend = '';
                    if (showDetailsInPopup) {
                        action = {
                            action: () => {
                                this.messageService.dismiss();
                            }
                        };
                        detailAppend = '<p><span class="toastAction">' + this.translate.instant('SHOW_ERROR_DETAILS') + '</span></p>';
                    }
                    const detail = msg + "\n" + this.translate.instant(`API_ERR_${apiError.error.status}`);
                    this.messageService.open(detail, null, {
                        duration: 3000,
                        horizontalPosition: "end",
                        verticalPosition: "top",
                        panelClass: ['bg-danger', 'snackBarMultiLine']
                    });
                }
            }
        );
    }

    public notifyGenericError(detail?: string, msgTranslateParams?: any, duration: number = 0): void {
        this.notify('ERROR_GENERIC', 'error');
    }

    modalSubject = new Subject<any[]>();
    modalSubject$ = this.modalSubject.asObservable();

    modalResponseSubject = new Subject<boolean>();
    modalResponseSubject$ = this.modalResponseSubject.asObservable();

    emitModal(msg){
      this.emitModalWithDetails(msg, "");
    }
    emitModalWithDetails(msg, details){
        this.modalSubject.next([msg, details]);
    }

    emitResponse(resp){
        this.modalResponseSubject.next(resp);
    }
}
